import { Activity } from 'lucide-react';
import { SectionIcon } from '../ui/SectionIcon.js';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FundSummary } from '@nemo/api';
import { STAGE_LABELS, formatRoundDate } from '@nemo/core/model';
import {
  formatRoundAmount,
  formatRoundLabel,
  othersPhrase,
  participantsOf,
} from '../dealroom/format.js';
import { PartnerLink, attributedPartner } from '../dealroom/PartnerLink.js';
import type { Round } from '../dealroom/types.js';
import { useTRPC } from '../trpc.js';
import { CoInvestments } from './CoInvestments.js';

export interface PaneActivityProps {
  fund: FundSummary;
  /** Set when the pane was opened from the grid's Recent investments cell. */
  focused: boolean;
}

/** How many rounds read as "what they have been doing" before the rest fold. */
const ROUNDS_SHOWN = 5;

/**
 * What the fund has actually been doing, which is the question a count of
 * recent rounds was only ever standing in for. Nothing here is editable: a
 * round is what Dealroom and the press said. The chips under it are the same
 * evidence read sideways — the stages and the sectors the rounds land in —
 * so a claim on the profile can be read against the behaviour above it.
 */
export function PaneActivity(props: PaneActivityProps): React.JSX.Element {
  const trpc = useTRPC();
  const fund = useQuery(trpc.funds.get.queryOptions({ id: props.fund.id }));
  const section = useRef<HTMLElement>(null);
  const [all, setAll] = useState(false);

  const rounds: readonly Round[] = fund.data?.investments ?? [];
  const shown = all ? rounds : rounds.slice(0, ROUNDS_SHOWN);
  const rest = rounds.length - shown.length;

  // Opened from the Recent investments cell, the pane is being opened *at*
  // this section, which is below the fold; it can only be scrolled to once the
  // rounds it is scrolling to have arrived.
  useEffect(() => {
    if (!props.focused || fund.data === undefined) return;
    section.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [props.focused, fund.data]);

  const tags = [
    ...props.fund.observedStages.map((stage) => STAGE_LABELS[stage]),
    ...props.fund.derivedSectors,
  ];

  return (
    <section className="pane-section" id="fund-investments" ref={section}>
      <h3 className="pane-section-title">
        <SectionIcon icon={Activity} />
        Activity
      </h3>

      <CoInvestments fund={props.fund} />
      {/* Two lists under one title need telling apart. */}
      <h4 className="pane-subtitle">Rounds</h4>

      {fund.isPending && <p className="subtle">Loading…</p>}
      {!fund.isPending && rounds.length === 0 && (
        <p className="subtle">No rounds have come in for this fund.</p>
      )}

      <div className="pane-list">
        {shown.map((round) => (
          <Investment key={round.id} round={round} fundId={props.fund.id} />
        ))}
      </div>

      {rest > 0 && (
        <button className="pane-add-link" type="button" onClick={() => setAll(true)}>
          +{rest} more
        </button>
      )}

      {tags.length > 0 && (
        <p className="pane-tags" title="Observed from the rounds above, not claimed by the fund">
          {tags.map((tag) => (
            <span className="chip-hollow" key={tag}>
              {tag}
            </span>
          ))}
        </p>
      )}
    </section>
  );
}

function Investment({ round, fundId }: { round: Round; fundId: string }): React.JSX.Element {
  const { ours, others } = participantsOf(round, fundId);
  const partners = ours.flatMap((participant) => {
    const partner = attributedPartner(participant);
    return partner === null ? [] : [partner];
  });
  const led = ours.some((participant) => participant.lead);
  const when = formatRoundDate(round);

  // This fund's side of the round first — whether it led, and who did it —
  // then who else was in it.
  const parts: React.ReactNode[] = [
    ...(partners.length > 0
      ? [
          <span key="partners">
            {led ? 'led by ' : 'partner: '}
            {partners.map((partner, index) => (
              <span key={partner.investorId}>
                {index > 0 && ', '}
                <PartnerLink partner={partner} />
              </span>
            ))}
          </span>,
        ]
      : led
        ? ['led']
        : []),
    ...(others.length > 0 ? [othersPhrase(others)] : []),
  ];

  return (
    <div className="round">
      <p className="round-line">
        {round.companyWebsite === null ? (
          <span className="round-company">{round.companyName}</span>
        ) : (
          <a className="round-company" href={round.companyWebsite} target="_blank" rel="noreferrer">
            {round.companyName}
          </a>
        )}
        {round.roundLabel !== null && (
          // Dealroom's own words, just not shouted — the same softening the
          // grid's Recent investments cell uses.
          <span className="round-part">{formatRoundLabel(round.roundLabel)}</span>
        )}
        <span className="round-part">{when === '' ? 'date unknown' : when}</span>
        <span className="spacer" />
        <span className="amount">{formatRoundAmount(round.amountEur)}</span>
      </p>

      {parts.length > 0 && (
        <p className="round-sub">
          {parts.map((part, index) => (
            <Fragment key={index}>
              {index > 0 && ' · '}
              {part}
            </Fragment>
          ))}
        </p>
      )}
    </div>
  );
}
